<script setup>
	import { onBeforeMount, ref, computed, watch } from 'vue';
	import { useMapStoreData, useFavoritesStore, useScreensaverStore, useAnalyticsStore } from '@/stores/index';
	import { useI18n } from 'vue-i18n';
	import closeIcon from '@/assets/close-marker-dark.svg';
	import nextIcon from '@/assets/right.svg';
	import resetIcon from '@/assets/retry-icon.svg';
	import checkIcon from '@/assets/check.svg';
	import flagIcon from '@/assets/flag.svg';
	import FlagLoader from './FlagLoader.vue';

	const { t } = useI18n();
	const API = import.meta.env.VITE_ITINERARY_API_URL;

	const mapStore = useMapStoreData();
	const showAiWizard = computed({
		get: () => mapStore.showAiWizard,
		set: (value) => (mapStore.showAiWizard = value),
	});
	const questionsData = ref([]);
	const isLoadingQuestions = ref(false);
	const questionsFetchInFlight = ref(false);
	const step = ref(1);
	const isLoading = ref(false);
	const isFinished = ref(false);
	const userSelections = ref({});
	const aiDescription = ref('');

	const markers = computed(() => {
		return Object.values(mapStore.markers).map((marker) => ({
			id: marker.properties.id,
			title: marker.properties.title,
		}));
	});

	const currentQuestion = computed(() => {
		return questionsData.value[step.value - 1] || null;
	});

	const currentAnswers = computed(() => {
		return currentQuestion.value?.answers?.[0] || [];
	});

	const isLastStep = computed(() => {
		return step.value >= questionsData.value.length;
	});

	const isFirstStep = computed(() => {
		return step.value <= 1;
	});

	const canProceed = computed(() => {
		const questionId = currentQuestion.value?.id;
		return (
			questionId &&
			userSelections.value[questionId] &&
			(Array.isArray(userSelections.value[questionId])
				? userSelections.value[questionId].length > 0
				: userSelections.value[questionId] !== '')
		);
	});

	const formattedDescription = computed(() => {
		if (!aiDescription.value) return [];

		return aiDescription.value
			.replace(/\s*([,.])\s*/g, '$1 ') // Remove spaces around commas and periods
			.replace(/\s+/g, ' ') // Replace multiple spaces with single space
			.trim()
			.split(/(?=(?<!\d)\d{1,3}\.\s)/) // Split before each number (1-3 digits, excluding years)
			.map((line) => {
				const trimmed = line.trim();
				const match = trimmed.match(/^(\d{1,3})\.\s*(.*)$/);
				if (match) {
					const num = parseInt(match[1], 10);
					if (num > 10) {
						return {
							number: null,
							text: trimmed,
						};
					}
					return {
						number: match[1],
						text: match[2],
					};
				}
				return {
					number: null,
					text: trimmed,
				};
			});
	});

	const handleSelection = (questionId, answerValue, isMultiple = false) => {
		if (isMultiple) {
			if (!userSelections.value[questionId]) {
				userSelections.value[questionId] = [];
			}
			const currentSelections = userSelections.value[questionId];
			const index = currentSelections.indexOf(answerValue);
			if (index > -1) {
				currentSelections.splice(index, 1);
			} else {
				currentSelections.push(answerValue);
			}
		} else {
			userSelections.value[questionId] = answerValue;
		}
	};

	const nextStep = () => {
		if (canProceed.value && !isLastStep.value) {
			step.value++;
		}
	};

	const prevStep = () => {
		if (!isFirstStep.value) {
			step.value--;
		}
	};

	const resetWizard = () => {
		step.value = 1;
		userSelections.value = {};
		isFinished.value = false;
		aiDescription.value = '';
	};

	const resetWizardAndFavorites = () => {
		resetWizard();
		useFavoritesStore().deleteAll();
		useFavoritesStore().closeFavoritesPanel();
		// Wizard stays open — same as opening again for a new run
		useAnalyticsStore().send('ai_wizz_triggered');
	};

	const loadQuestions = () => {
		if (questionsFetchInFlight.value) return;

		questionsFetchInFlight.value = true;
		isLoadingQuestions.value = true;

		const data = {
			request: {
				interface: 'LocationRequests',
				method: 'getAIQuestions',
				parameters: {
					destination: mapStore.destinationId,
				},
			},
		};

		console.log('[AiWizard] loading questions for destination', mapStore.destinationId);

		return fetch(API, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		})
			.then((response) => response.json())
			.then((result) => {
				const questions = result?.success?.questions?.questions ?? [];
				questionsData.value = questions;
				console.log('[AiWizard] loaded questions:', questions.length);
			})
			.catch((error) => {
				console.error('[AiWizard] failed to load questions:', error);
				questionsData.value = [];
			})
			.finally(() => {
				isLoadingQuestions.value = false;
				questionsFetchInFlight.value = false;
			});
	};

	onBeforeMount(() => {
		loadQuestions();
	});

	const prepackQuestionsData = () => {
		return questionsData.value.map((question) => {
			const rawSelection = userSelections.value[question.id];
			const userSelection = Array.isArray(rawSelection) ? Array.from(rawSelection) : rawSelection;
			return {
				...question.back,
				selected: userSelection,
				id: question.id,
				answers: question.answers[0].map((answer) => {
					return { value: answer.back.value };
				}),
			};
		});
	};

	const sendAnswers = () => {
		isLoading.value = true;
		const data = {
			request: {
				interface: 'LocationRequests',
				method: 'sugestPois',
				parameters: {
					destination: mapStore.destinationId,
					lang: mapStore.currentLanguage,
					places: markers.value,
					questions: { _rawValue: prepackQuestionsData(), value: prepackQuestionsData() },
					llmCommand: 'test',
				},
			},
		};

		fetch(API, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		})
			.then((response) => response.json())
			.then((result) => {
				if (result.success) {
					const pois = result.success?.pois;
					pois.forEach((poi) => useFavoritesStore().toggleFavorites(poi.toString()));
					mapStore.showMap = true;
					isFinished.value = true;
					aiDescription.value = result.success?.description;
					useFavoritesStore().isOpenPanel = true;
				}
			})
			.catch((error) => {
				console.error('Error sending answers:', error);
			})
			.finally(() => {
				isLoading.value = false;
			});
	};

	watch(showAiWizard, (newValue) => {
		if (newValue) {
			if (questionsData.value.length === 0 && !questionsFetchInFlight.value) {
				loadQuestions();
			}
			useAnalyticsStore().send('ai_wizz_triggered');
			if (useFavoritesStore().favorites.length > 0) {
				useFavoritesStore().favorites = [];
			}
			if (useFavoritesStore().isOpenPanel) {
				useFavoritesStore().closeFavoritesPanel();
			}
		} else {
			if (!isFinished.value) {
				useAnalyticsStore().send('skip_wizzard', { step: step.value });
			}
			resetWizard();
		}
	});

	watch(
		() => useScreensaverStore().showSreensaver,
		(newValue) => {
			if (newValue) {
				showAiWizard.value = false;
			}
		}
	);

	watch(isFinished, (newValue) => {
		if (newValue) {
			useAnalyticsStore().send('ai_wizz_completed');
		}
	});

	watch(
		() => mapStore.isOnline,
		(isOnline) => {
			if (isOnline && questionsData.value.length === 0 && !questionsFetchInFlight.value) {
				console.log('[AiWizard] back online — retrying question load');
				loadQuestions();
			}
		}
	);
</script>

<template>
	<button
		@click="showAiWizard = !showAiWizard"
		class="ai-wizard-bubble"
		:class="{ 'ai-wizard-bubble-bottom': mapStore.destinationId == 17 }"
		:aria-expanded="showAiWizard"
		aria-label="Toggle AI Wizard"
		type="button"
	>
		{{ showAiWizard ? t('ai.bubble.whattodo') : t('ai.bubble.try') }}
	</button>
	<Transition name="ai-wizard">
		<div
			v-if="showAiWizard"
			class="ai-wizard-container"
			:class="{ 'ai-wizard-container-bottom': mapStore.destinationId == 17 }"
		>
			<div v-if="isLoading || isLoadingQuestions" class="ai-wizard-container-loading">
				<FlagLoader />
			</div>

			<div class="ai-wizard-container-content" v-else-if="currentQuestion && !aiDescription">
				<div class="question-header">
					<h2>{{ t(currentQuestion.front.caption_up) }}</h2>
					<h3 v-if="currentQuestion.front.caption_up">
						{{ t(currentQuestion.front.caption_down) }}
					</h3>
				</div>

				<div class="question-answers">
					<div v-for="answer in currentAnswers" :key="answer.front.caption" class="answer-option">
						<div v-if="currentQuestion.front.type === 'radio'" class="radio-group">
							<input
								:id="`${currentQuestion.id}-${answer.front.caption}`"
								type="radio"
								:name="currentQuestion.id"
								:value="answer.back.value"
								:checked="userSelections[currentQuestion.id] === answer.back.value"
								@change="handleSelection(currentQuestion.id, answer.back.value, false)"
							/>
							<label :for="`${currentQuestion.id}-${answer.front.caption}`" class="answer-label">
								<span class="answer-text">{{ t(answer.front.caption) }}</span>
							</label>
						</div>

						<div v-else-if="currentQuestion.front.type === 'checkbox'" class="checkbox-group">
							<input
								:id="`${currentQuestion.id}-${answer.front.caption}`"
								type="checkbox"
								:value="answer.back.value"
								:checked="userSelections[currentQuestion.id]?.includes(answer.back.value)"
								@change="handleSelection(currentQuestion.id, answer.back.value, true)"
							/>
							<label :for="`${currentQuestion.id}-${answer.front.caption}`" class="answer-label">
								<span class="answer-text">{{ t(answer.front.caption) }}</span>
							</label>
						</div>
					</div>
				</div>
			</div>

			<div class="ai-wizard-container-description" v-else-if="aiDescription">
				<div v-for="(line, index) in formattedDescription" :key="index" class="description-line">
					<span v-if="line.number">{{ line.number }}.</span>
					{{ line.text }}
				</div>
			</div>

			<div class="ai-wizard-container-footer" v-if="!isLoading && !isLoadingQuestions">
				<button v-if="!isFinished" class="exit" @click="showAiWizard = false">
					<img :src="closeIcon" alt="Exit" />
					{{ t('ai.button.cancel') }}
				</button>
				<button v-if="!isFinished && currentQuestion" class="back" @click="prevStep" :disabled="isFirstStep">
					<img :src="nextIcon" alt="Back" />
					{{ t('ai.button.back') }}
				</button>
				<button v-if="currentQuestion && !isLastStep" class="next" @click="nextStep" :disabled="!canProceed">
					{{ t('ai.button.next') }}
					<img :src="nextIcon" alt="Next" />
				</button>
				<button v-else-if="currentQuestion && !isFinished" class="finish" @click="sendAnswers" :disabled="!canProceed">
					{{ t('ai.button.generate') }}
					<img :src="checkIcon" alt="Generate" />
				</button>
				<button v-if="isFinished" class="reset" @click="resetWizardAndFavorites">
					{{ t('ai.button.retry') }}
					<img style="filter: invert(0%)" :src="resetIcon" alt="Reset" />
				</button>
				<button v-if="isFinished" class="finish" @click="showAiWizard = false">
					{{ t('ai.button.finish') }}
					<img :src="flagIcon" alt="Finish" />
				</button>
			</div>

			<div v-if="currentQuestion && !aiDescription && !isLoading && !isLoadingQuestions" class="ai-wizard-container-steps">
				<p>{{ step }}</p>
			</div>
		</div>
	</Transition>
</template>

<style scoped lang="scss">
	@import '../assets/style/mixins.scss';
	@import '../assets/style/components/_aiWizard.scss';

	@media only screen and (min-width: 1080px) and (max-width: 1080px) and (min-height: 1920px) and (max-height: 1920px) {
		.destination-17 {
			.ai-wizard-container {
				height: 960px;
				bottom: 120px;
				top: auto;

				border-radius: 0 50px 0 0;
			}
		}
	}
</style>
